/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const formatTime = (h, m) => {
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${hh}:${mm}`;
};

const speakTime = (h, m) => {
  if (m === 0) return `Pukul ${h}`;
  if (m === 30) return `Pukul ${h} lewat tiga puluh menit`;
  return `Pukul ${h} lewat ${m} menit`;
};

const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const ClockSVG = ({ h, m, size = 240 }) => {
  const cx = size / 2, cy = size / 2, r = size / 2 - 10;
  const minAngle = (m / 60) * 360 - 90;
  const hourAngle = ((h % 12) / 12) * 360 + (m / 60) * 30 - 90;
  const minRad = (minAngle * Math.PI) / 180;
  const hourRad = (hourAngle * Math.PI) / 180;
  const minX = cx + Math.cos(minRad) * (r * 0.85);
  const minY = cy + Math.sin(minRad) * (r * 0.85);
  const hourX = cx + Math.cos(hourRad) * (r * 0.55);
  const hourY = cy + Math.sin(hourRad) * (r * 0.55);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Outer */}
      <circle cx={cx} cy={cy} r={r} fill="white" stroke="#92400E" strokeWidth="6" />
      <circle cx={cx} cy={cy} r={r - 8} fill="#FEF3C7" stroke="#FFD700" strokeWidth="2" />
      {/* Hour markers + numbers */}
      {[...Array(12)].map((_, i) => {
        const angle = ((i * 30) - 90) * Math.PI / 180;
        const x1 = cx + Math.cos(angle) * (r - 18);
        const y1 = cy + Math.sin(angle) * (r - 18);
        const x2 = cx + Math.cos(angle) * (r - 8);
        const y2 = cy + Math.sin(angle) * (r - 8);
        const xn = cx + Math.cos(angle) * (r - 32);
        const yn = cy + Math.sin(angle) * (r - 32);
        const num = i === 0 ? 12 : i;
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#92400E" strokeWidth="3" strokeLinecap="round" />
            <text x={xn} y={yn + 6} fontSize="22" fontWeight="900" fontFamily="Fredoka" textAnchor="middle" fill="#92400E">{num}</text>
          </g>
        );
      })}
      {/* Hour hand */}
      <line x1={cx} y1={cy} x2={hourX} y2={hourY} stroke="#1F2937" strokeWidth="7" strokeLinecap="round" />
      {/* Minute hand */}
      <line x1={cx} y1={cy} x2={minX} y2={minY} stroke="#DC2626" strokeWidth="4" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx={cx} cy={cy} r="6" fill="#1F2937" />
    </svg>
  );
};

const BelajarJam = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState({ h: 3, m: 0 });
  const [opts, setOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const newRound = useCallback(() => {
    const h = Math.floor(Math.random() * 12) + 1;
    const minutes = [0, 15, 30, 45];
    const m = minutes[Math.floor(Math.random() * minutes.length)];
    const wrongs = new Set();
    while (wrongs.size < 3) {
      const wh = Math.floor(Math.random() * 12) + 1;
      const wm = minutes[Math.floor(Math.random() * minutes.length)];
      const key = `${wh}:${wm}`;
      if (key !== `${h}:${m}` && !wrongs.has(key)) wrongs.add(key);
    }
    const allKeys = [...wrongs, `${h}:${m}`].sort(() => Math.random() - 0.5);
    const all = allKeys.map(k => { const [hh, mm] = k.split(":").map(Number); return { h: hh, m: mm }; });
    setTarget({ h, m }); setOpts(all); setFeedback(null);
    setTimeout(() => { if (soundOn) speakText("Jam what is ini?"); }, 300);
  }, [soundOn]);

  useEffect(() => { newRound(); /* eslint-disable-next-line */ }, []);

  const handlePick = (o) => {
    if (feedback) return;
    if (o.h === target.h && o.m === target.m) {
      setFeedback({ type: "ok", text: `Benar! ${formatTime(o.h, o.m)}` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! ${speakTime(o.h, o.m)}`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${formatTime(o.h, o.m)}` });
      if (soundOn) speakText(`The correct answer is ${speakTime(target.h, target.m)}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) { setWon(true); recordGameWon("jam"); }
      else { setRound(r => r + 1); newRound(); }
    }, 1800);
  };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="jam-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">⏰</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); newRound(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const clockSize = Math.min(winW - 80, 380);

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="jam-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">⏰ {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <motion.div key={round} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-4 w-full flex flex-col items-center mb-3">
        <p className="font-fredoka text-gold-light/70 text-sm mb-2">Jam berapa ini?</p>
        <ClockSVG h={target.h} m={target.m} size={clockSize} />
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`jam-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full max-w-2xl">
        {opts.map((o, i) => (
          <motion.button key={`${o.h}-${o.m}-${i}`} data-testid={`jam-opt-${i}`}
            onClick={() => handlePick(o)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="ingredient-3d quiz-option-btn-large text-center text-slate-800 font-fredoka font-bold">
            {formatTime(o.h, o.m)}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default BelajarJam;
